import * as THREE from 'three';
import { GLTFExporter } from 'three/addons/exporters/GLTFExporter.js';
import type { Retargeter } from '../retarget/retarget.ts';

export interface GlbExportInput {
  /** Top-level objects to export: the puppet meshes, or the source skeleton group. */
  roots: THREE.Object3D[];
  /** When non-empty, these are applied each baked frame (mesh/puppet export). */
  retargeters: Retargeter[];
  /** Bones to sample when there are no puppets (raw skeleton export). */
  sourceBones: THREE.Object3D[];
  startFrame: number;
  endFrame: number;
  fps: number;
  /**
   * Bake rate for the exported clip; defaults to `fps`. Lower values downsample
   * high-rate takes (e.g. Move.AI 180) to a lighter file over the same seconds.
   */
  sampleFps?: number;
  /**
   * Bake a position track for EVERY sampled bone, not just the hips. Required
   * when proportion matching is on (it drives all joint positions per frame).
   */
  bakeAllPositions?: boolean;
  /** Drive the source mixers to a given time (seconds). */
  setTime: (seconds: number) => void;
  /**
   * Runs after the retargeters each sampled frame — contact-pin IK and other
   * post-retarget adjustments hook in here so they bake into the clip.
   */
  afterPose?: (seconds: number) => void;
  /** Viewport camera + its look-at target — embedded as a glTF camera node. */
  camera: THREE.PerspectiveCamera;
  cameraTarget: THREE.Vector3;
  /**
   * When set, the camera program is baked instead of the static viewport pose:
   * sampled per exported frame into position/quaternion tracks on the camera
   * node. (glTF cannot animate fov — the first frame's fov is used.)
   */
  sampleCamera?: (
    seconds: number
  ) => { position: THREE.Vector3; target: THREE.Vector3; fov: number } | null;
  filename: string;
}

interface SampledBone {
  bone: THREE.Object3D;
  quat: Float32Array;
  pos: Float32Array | null;
}

const isHips = (name: string): boolean => /hips?$|pelvis/i.test(name);

/**
 * Bake the (possibly retargeted) animation into a single clip keyed by bone UUID
 * — which sidesteps the duplicate bone names that broke multi-actor export — and
 * write a binary .glb containing the meshes/skeletons plus a camera node.
 *
 * The caller is responsible for pausing the render loop and restoring the live
 * playhead afterwards; this function reparents the roots only transiently.
 */
export async function bakeAndExportGlb(input: GlbExportInput): Promise<void> {
  const { roots, retargeters, fps, setTime, startFrame, endFrame } = input;
  const start = Math.min(startFrame, endFrame);
  const end = Math.max(startFrame, endFrame);
  if (end - start < 1) throw new Error('Marked range is empty — set Start and End frames first.');

  const usePuppets = retargeters.length > 0;
  // Bake at sampleFps over the marked range's SECONDS (frames are timeline-fps).
  const sampleFps = input.sampleFps ?? fps;
  const startSec = start / fps;
  const rangeSec = (end - start) / fps;
  const frames = Math.max(2, Math.round(rangeSec * sampleFps) + 1);
  const sampleTime = (i: number): number => startSec + i / sampleFps;
  const times = new Float32Array(frames);

  // --- 1. Which bones to sample, with pre-allocated track buffers. -------------
  const sampled: SampledBone[] = [];
  if (usePuppets) {
    for (const rt of retargeters) {
      const hips = rt.pairs.find((p) => isHips(p.target.name) || isHips(p.source.name))?.target;
      for (const pair of rt.pairs) {
        sampled.push({
          bone: pair.target,
          quat: new Float32Array(frames * 4),
          pos:
            pair.target === hips || input.bakeAllPositions
              ? new Float32Array(frames * 3)
              : null,
        });
      }
    }
  } else {
    for (const bone of input.sourceBones) {
      const rootBone = !((bone.parent as THREE.Bone | null)?.isBone);
      sampled.push({
        bone,
        quat: new Float32Array(frames * 4),
        pos: rootBone ? new Float32Array(frames * 3) : null,
      });
    }
  }

  // Animated camera buffers (program export) — posed via a scratch camera so
  // lookAt produces the quaternion for each frame.
  const camSampler = input.sampleCamera ?? null;
  const camPos = camSampler ? new Float32Array(frames * 3) : null;
  const camQuat = camSampler ? new Float32Array(frames * 4) : null;
  const scratchCam = new THREE.PerspectiveCamera();
  let firstFov: number | null = null;

  // --- 2. Sample every bake frame in the marked range. -------------------------
  for (let i = 0; i < frames; i++) {
    times[i] = i / sampleFps;
    setTime(sampleTime(i));
    if (usePuppets) for (const rt of retargeters) rt.apply();
    input.afterPose?.(sampleTime(i));
    for (const s of sampled) {
      s.bone.quaternion.toArray(s.quat, i * 4);
      if (s.pos) s.bone.position.toArray(s.pos, i * 3);
    }
    if (camSampler && camPos && camQuat) {
      const sample = camSampler(sampleTime(i));
      if (sample) {
        scratchCam.position.copy(sample.position);
        scratchCam.lookAt(sample.target);
        scratchCam.position.toArray(camPos, i * 3);
        scratchCam.quaternion.toArray(camQuat, i * 4);
        firstFov ??= sample.fov;
      }
    }
  }

  // --- 3. Build a UUID-keyed clip. ---------------------------------------------
  const tracks: THREE.KeyframeTrack[] = [];
  for (const s of sampled) {
    tracks.push(new THREE.QuaternionKeyframeTrack(`${s.bone.uuid}.quaternion`, times, s.quat));
    if (s.pos) tracks.push(new THREE.VectorKeyframeTrack(`${s.bone.uuid}.position`, times, s.pos));
  }
  // (The clip is assembled after the camera node below, so its baked tracks —
  // if any — are included too.)

  // Park objects at the first frame so exported node transforms match clip[0].
  setTime(startSec);
  if (usePuppets) for (const rt of retargeters) rt.apply();
  input.afterPose?.(startSec);

  // --- 4. Assemble a temporary export root (reparent + camera node). -----------
  const exportRoot = new THREE.Group();
  exportRoot.name = 'AKA_Scene';
  const originalParents = roots.map((r) => r.parent);
  for (const r of roots) exportRoot.add(r);

  const cam = new THREE.PerspectiveCamera(
    firstFov ?? input.camera.fov,
    input.camera.aspect,
    input.camera.near,
    input.camera.far
  );
  if (camSampler && camPos && camQuat) {
    // Animated program camera: node parked at frame 0, driven by baked tracks.
    cam.name = 'ProgramCamera';
    cam.position.fromArray(camPos, 0);
    cam.quaternion.fromArray(camQuat, 0);
    tracks.push(new THREE.VectorKeyframeTrack(`${cam.uuid}.position`, times, camPos));
    tracks.push(new THREE.QuaternionKeyframeTrack(`${cam.uuid}.quaternion`, times, camQuat));
  } else {
    cam.name = 'ViewportCamera';
    cam.position.copy(input.camera.position);
    cam.up.copy(input.camera.up);
    cam.lookAt(input.cameraTarget);
  }
  exportRoot.add(cam);
  exportRoot.updateMatrixWorld(true);

  const clip = new THREE.AnimationClip('mocap_segment', (frames - 1) / sampleFps, tracks);

  // --- 5. Export, then restore the scene graph. --------------------------------
  try {
    const exporter = new GLTFExporter();
    const glb = await new Promise<ArrayBuffer>((resolve, reject) => {
      exporter.parse(
        exportRoot,
        (result) => resolve(result as ArrayBuffer),
        (err) => reject(err),
        { binary: true, animations: [clip] }
      );
    });
    downloadBlob(new Blob([glb], { type: 'model/gltf-binary' }), input.filename);
  } finally {
    for (let i = 0; i < roots.length; i++) originalParents[i]?.add(roots[i]);
  }
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
